import { CssBaseline, CssVarsProvider } from '@mui/joy';
import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import TimeAgo from 'javascript-time-ago';
import ko from 'javascript-time-ago/locale/ko';
import React from 'react';
import ReactDOM from 'react-dom/client';

import MinddSnackbarProvider from '@MP/shared/components/MinddSnackbarProvider';
import theme from '@MP/theme';

import App from './App';
import { navigateTo } from './app/router';
import './index.css';
import { initClarity } from './shared/utils/initClarity';
import WebTokenManager from './shared/utils/WebTokenManager';

initClarity();

TimeAgo.addDefaultLocale(ko);
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount: number, error: unknown) => {
        const axiosError = error as AxiosError;
        if (axiosError.response?.status === 401 || axiosError.response?.status === 404) {
          return false;
        } else {
          return failureCount < 2;
        }
      },
      gcTime: 1000 * 60 * 60 * 3,
      staleTime: 1000 * 60 * 60 * 3,
      placeholderData: (previousData: any) => previousData,
    },
  },
  queryCache: new QueryCache({
    onError: (error: unknown) => {
      const axiosError = error as AxiosError;
      if (axiosError.response) {
        switch (axiosError.response.status) {
          case 401:
            // 이미 /login 이면 리다이렉트 루프 방지를 위해 스킵.
            // /m/* 은 토큰 링크로 진입하는 환자 화면이라 로그인 페이지가 없다 — 각 화면이 만료/무효 상태를 직접 보여준다.
            if (window.location.pathname !== '/login' && !window.location.pathname.startsWith('/m/')) {
              WebTokenManager.clear();
              const redirectUrl = window.location.href;
              navigateTo(`/login?redirectUrl=${redirectUrl}`, { replace: true });
            }
            break;
          default:
            break;
        }
      }
    },
  }),
});

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <CssVarsProvider theme={theme}>
        <CssBaseline />
        <MinddSnackbarProvider>
          <App />
        </MinddSnackbarProvider>
      </CssVarsProvider>
    </QueryClientProvider>
  </React.StrictMode>
);
